import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PopupProps {
    isOpen: boolean;
    onClose?: () => void;
    header?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
}

const Popup: React.FC<PopupProps> = ({ 
    isOpen, 
    onClose, 
    header, 
    children, 
    footer, 
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy 
}) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (isOpen) {
            // Store the currently focused element
            previousActiveElement.current = document.activeElement as HTMLElement;
            
            // Prevent body scrolling
            document.body.style.overflow = 'hidden';
            
            // Focus the dialog
            if (dialogRef.current) {
                dialogRef.current.focus();
            }
        } else {
            document.body.style.overflow = 'unset';
            
            // Restore focus to the previously focused element
            if (previousActiveElement.current) {
                previousActiveElement.current.focus();
            }
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen && onClose) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    // Trap focus within the dialog
    useEffect(() => {
        if (!isOpen) return;

        const handleTabKey = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            const focusableElements = dialogRef.current?.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            if (!focusableElements || focusableElements.length === 0) return;

            const firstElement = focusableElements[0] as HTMLElement;
            const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

            if (event.shiftKey) {
                if (document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, [isOpen]);

    if (!isOpen) return null;


    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(4px)'
            }}
            onClick={onClose}
            role="presentation"
        >
            {/* Close Button */}
            {onClose && (
                <button
                    onClick={onClose}
                    className="cursor-pointer absolute top-4 right-4 text-white hover:text-gray-200 text-3xl font-bold z-10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded"
                    aria-label="Close dialog"
                    type="button"
                >
                    ×
                </button>
            )}
            <div 
                ref={dialogRef}
                className="bg-white rounded-lg shadow-xl mx-4 relative"
                style={{ width: '80vw', maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                tabIndex={-1}
            >


                {/* Header */}
                {header && (
                    <div className="px-6 py-4 border-b border-gray-200">
                        {header}
                    </div>
                )}

                {/* Content */}
                <div className="px-6 py-4">
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className="px-6 py-4 border-t border-gray-200">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Popup;
